/**
 * This is a client for the menus backend service.
 *
 * These apis are public (no-auth) and are shared by both, the dashboard and the app.
 */
import {
  GetAllResolvedEdiblesResponse,
  GetDishDetailRequest,
  GetDishDetailResponse,
  GetMenuAppMetadataRequest,
  GetMenuAppMetadataResponse,
  GetMenuAppRestaurantDataRequest,
  GetMenuAppRestaurantDataResponse,
  GetMenuByIdRequest,
  GetMenuByMenuUrlRequest,
  GetMenuResponseV2,
  ResolveEdibleRequest,
  ResolvedEdible,
} from "api-types";
import { AxiosInstance } from "axios";

/** Fetches a single menu by restaurant UUID and menu ID. */
export const getMenuById = async (
  client: AxiosInstance,
  restaurantUuid: string,
  menuId: string
): Promise<GetMenuResponseV2 | undefined> => {
  const request: GetMenuByIdRequest = { restaurantUuid, menuId };
  const { data, status } = await client.post<GetMenuResponseV2>(
    "/menus/get-menu-by-id",
    request,
    {
      validateStatus: (status) =>
        (status >= 200 && status < 300) || status === 404,
    }
  );

  if (status === 404) {
    return undefined;
  }

  return data;
};

/** Resolves menus from URL slug via menu-urls-v2. */
export const getMenuByMenuUrl = async (
  client: AxiosInstance,
  urlSlug: string,
  menuId?: string
): Promise<GetMenuResponseV2 | undefined> => {
  const request: GetMenuByMenuUrlRequest = {
    urlSlug,
    menuId,
  };
  const { data, status } = await client.post<GetMenuResponseV2>(
    "/menus/get-menu-by-menu-url",
    request,
    {
      validateStatus: (status) =>
        (status >= 200 && status < 300) || status === 404,
    }
  );

  if (status === 404) {
    return undefined;
  }

  return data;
};

/**
 * Returns all taxonomy data and an optional list of ingredient details.
 *
 * @param ingredientIds an optional list of ingredient ids for which we want to fetch ingredient details.
 * @returns
 */
export const getMenuAppMetadata = async (
  client: AxiosInstance,
  igfdTaxIngredientIds?: string[]
): Promise<GetMenuAppMetadataResponse> => {
  const getMenuAppMetadataRequest: GetMenuAppMetadataRequest = {
    igfdTaxIngredientIds,
  };
  const { data } = await client.post<GetMenuAppMetadataResponse>(
    "/menus/get-menu-app-metadata",
    getMenuAppMetadataRequest
  );
  return data;
};

/** Returns Restaurant info by url slug */
export const getMenuAppRestaurantData = async (
  client: AxiosInstance,
  urlSlug: string
): Promise<GetMenuAppRestaurantDataResponse | undefined> => {
  const getMenuAppRestaurantDataRequest: GetMenuAppRestaurantDataRequest = {
    urlSlug,
  };
  const { data, status } = await client.post<GetMenuAppRestaurantDataResponse>(
    "/menus/get-menu-app-restaurant-data",
    getMenuAppRestaurantDataRequest,
    {
      validateStatus: (status) =>
        (status >= 200 && status < 300) || status === 404,
    }
  );

  if (status === 404) {
    return undefined;
  }

  return data;
};

/** Returns resolved edibled (dishes and products) of a restaurant */
export const getAllResolvedEdibles = async (
  client: AxiosInstance,
  restaurantUuid: string
): Promise<GetAllResolvedEdiblesResponse | undefined> => {
  const { data } = await client.post("/menus/get-all-resolved-edibles", {
    restaurantUuid,
  });
  return data;
};

/** Returns the details of one dish that the menu response leaves out */
export const getDishDetail = async (
  client: AxiosInstance,
  restaurantUuid: string,
  dishId: string
): Promise<GetDishDetailResponse> => {
  const request: GetDishDetailRequest = { restaurantUuid, dishId };
  const { data } = await client.post<GetDishDetailResponse>(
    "/menus/get-dish-detail",
    request
  );
  return data;
};

/**
 * Given a set of constituent ingredients and products, returns the
 * resolved allergens and preferences
 */
export const resolveEdible = async (
  client: AxiosInstance,
  request: ResolveEdibleRequest
): Promise<ResolvedEdible | undefined> => {
  const { data } = await client.post("/menus/resolve-edible", request);
  return data;
};
